import axios from 'axios';
import ky from 'ky';
import * as Sentry from 'utils/errorTracking';

import { getAPI } from 'utils/helpers';
import apiClient from 'lib/apiClient';
import { setJwt, setRefreshToken } from 'lib/tokenStorage';

export const loginRequest = async ({ username, password, fingerprint, mfaCode }) => {
  try {
    const payload = {
      username,
      password,
      fingerprint,
      mfaCode,
    };
    const response = await axios.post(`${getAPI()}/user_svc/user/loginV2`, payload);

    if (response.data && response.data.jwt && response.data.refresh) {
      setJwt(response.data.jwt);
      setRefreshToken(response.data.refresh);
    }

    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Login request failed:', error);
    throw error;
  }
};

export const federatedLoginRequestv2 = async ({ idToken, fingerprint, mfaCode }) => {
  try {
    const data = {
      idToken,
      fingerprint,
      mfaCode,
    };

    const response = await axios.post(`${getAPI()}/user_svc/user/floginV2`, data);

    if (response.data && response.data.jwt && response.data.refresh) {
      setJwt(response.data.jwt);
      setRefreshToken(response.data.refresh);
    }

    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Federated login request failed:', error);
    throw error;
  }
};

export const validateUserRequest = async () => {
  try {
    const response = await apiClient.get(`${getAPI()}/user_svc/user/validateJwt`);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('User validation request failed:', error);
    throw error;
  }
};

export const forgotPasswordRequest = async (email) => {
  try {
    const response = await axios.post(`${getAPI()}/user_svc/user/forgotPassword`, email, {
      headers: {
        'Content-Type': 'text/plain',
      },
    });
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Forgot password request failed:', error);
    throw error;
  }
};

export const getAssociatedOfferings = async ({ primaryUserId }) => {
  try {
    const response = await ky(
      `${getAPI()}/primary_facade/v2/users/${primaryUserId}/associatedOfferings`,
    ).json();
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to get associated offerings:', error);
    return [];
  }
};

export const getUserRequest = async () => {
  try {
    const response = await ky(`${getAPI()}/user_svc/user/validateJwt`).json();
    return response;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getCurrentUserRequest = async () => {
  try {
    const response = await apiClient.get(`${getAPI()}/primary_facade/v2/users/current`);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to get current user:', error);
    throw error;
  }
};

export const isSubscribedRequest = async (productName, primaryUserId, cookie) => {
  try {
    const response = await axios.head(
      `${getAPI()}/subscription/subscription?productName=${productName}&primaryUserId=${primaryUserId}`,
      {
        headers: cookie
          ? {
              cookie,
            }
          : {},
        withCredentials: !!cookie,
      },
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to check subscription status:', error);
    throw error;
  }
};

export const signupRequest = async ({
  email,
  firstName,
  lastName,
  password,
  referralCode,
  updates,
  providerId,
  fingerprint,
}) => {
  try {
    const data = {
      email,
      firstName,
      lastName,
      password,
      referralCode,
      subscribedToNewsletter: updates,
      federatedProvider: providerId,
      fingerprint,
    };

    const response = await axios.post(`${getAPI()}/user_svc/user`, data);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Signup request failed:', error);
    throw error;
  }
};

export const sendEmailVerificationRequest = async () => {
  try {
    const response = await apiClient.post(
      `${getAPI()}/primary_facade/v2/users/current/sendEmailConfirmation`,
    );
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to send email verification:', error);
    throw error;
  }
};

export const saveIndustryPreferencesRequest = async (data) => {
  try {
    const response = await apiClient.post(
      `${getAPI()}/primary_facade/v2/users/${
        data.userId
      }/preferences/action/SAVE_INDUSTRY_PREFERENCES`,
      data,
    );
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to save industry preferences:', error);
    throw error;
  }
};

export const saveVerticalPreferencesRequest = async (data) => {
  try {
    const response = await apiClient.post(
      `${getAPI()}/primary_facade/v2/users/${
        data.userId
      }/preferences/action/SAVE_VERTICAL_PREFERENCES`,
      data,
    );
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to save vertical preferences:', error);
    throw error;
  }
};

export const getUserById = async (id) => {
  try {
    const response = await apiClient.get(`${getAPI()}/primary_facade/v2/users/${id}`);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to get user by ID:', error);
    throw error;
  }
};

export const verifyUser = async (id) => {
  try {
    const response = await apiClient.post(`${getAPI()}/user_svc/partner/${id}/verify`);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('Failed to verify user:', error);
    throw error;
  }
};

export const getSalesAgentCalendar = async () => {
  try {
    const url = `${getAPI()}/crm_manager/salesAgents/calendar`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
